import React, {useState, useCallback} from 'react';
import {useTranslation} from 'react-i18next';

// Stores
import useCartStore from '../../store/useCartStore';

// Alerts
import * as alertify from '../../components/Alerts/lib/alertify';

// Components
import CartContentTable from '../../components/CartContentTable/CartContentTable';
import DisplayCartData from '../../components/DisplayCartData/DisplayCartData';
import RemoveFromCart from '../../components/RemoveFromCart/RemoveFromCart';
import Content from '../../components/SearchContainer/SearchContainer';
import Table from '../../components/Table/Table';
import CheckBox from '../../components/CheckBox/CheckBox';
import TextDiv from '../../components/TextDiv/TextDiv';
import Checkout from '../../components/Checkout/Checkout';

// Data
import getColumns from './data/columns';

// Libs
import getNextWorkingDay from '../../order/libs/getNextWorkingDay.order';

// API
import orderApi from '../../order/api/order.api.order';

// Error
import getErrorMessageFromErrorCode from '../../lib/getErrorMessageFromErrorCode';

// Currency
import formatCurrency from '../../lib/formatCurrency';

const CartContainer = () => {
  const {t} = useTranslation();
  // Get state and actions from store
  const {
    items,
    removeItem,
    toggleItemSelection,
    selectAll,
    deselectAll,
    removeSelected,
    clear,
    getItemCount,
    getTotal,
    allSelected,
  } = useCartStore();

  const [comment, setComment] = useState('');
  const [orderNo, setOrderNo] = useState('');
  const [disabled, setDisabled] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const itemsCount = getItemCount();
  const total = getTotal();
  const isAllSelected = allSelected();

  const handleRemove = useCallback(
    (id) => {
      removeItem(id);
    },
    [removeItem]
  );

  const handleSelect = useCallback(
    (id) => {
      toggleItemSelection(id);
    },
    [toggleItemSelection]
  );

  const handleSelectAll = useCallback(() => {
    if (isAllSelected) {
      deselectAll();
    } else {
      selectAll();
    }
  }, [isAllSelected, selectAll, deselectAll]);

  const handleRemoveSelected = useCallback(() => {
    removeSelected();
  }, [removeSelected]);

  const handleCommentChange = useCallback((newComment) => {
    setComment(newComment);
  }, []);

  const handleOrderNumberChange = useCallback((orderNumber) => {
    setOrderNo(orderNumber);
    setDisabled(!orderNumber);
  }, []);

  const handleSubmit = useCallback(async () => {
    if (isSubmitting) return;

    setDisabled(true);
    setIsSubmitting(true);

    try {
      const orderLines = items.map((tyre) => ({
        catalogNumber: tyre.catalogNumber,
        rowQuantity: tyre.cartQuantity,
        confirmedDeliveryDate: tyre.selectedDeliveryDate || getNextWorkingDay(),
      }));

      const req = await orderApi({
        order: {
          documentID: orderNo,
          comment: comment,
          orderLines,
        },
      });

      const status = Number(req.contiOrder.status);

      if (status < 1) {
        alertify.success(t('orderCreated'));
        clear();
        setComment('');
        setOrderNo('');
      } else {
        const errorMessage = getErrorMessageFromErrorCode(status);
        alertify.error(errorMessage);
      }
    } catch (error) {
      alertify.error(t('errorHappened'));
    } finally {
      setDisabled(false);
      setIsSubmitting(false);
    }
  }, [isSubmitting, orderNo, comment, items, clear, t]);

  return (
    <Content>
      <DisplayCartData cartItemsCount={itemsCount} cartTotalPrice={total} />
      <CartContentTable>
        <Table
          loading={false}
          columns={getColumns(t)}
          size="medium"
          headerSize="small"
          noMargin={false}
          labelSpace={false}
          theme="white"
          noDataContent={t('noItemsInCart')}
        >
          {(TableRow) =>
            items.map((row) => (
              <TableRow
                key={row._id}
                id={row._id}
                noMargin={false}
                clickable={false}
              >
                {(TableCol) => [
                  <TableCol key="check" span={1}>
                    <CheckBox
                      selected={row.selected}
                      onSelect={() => handleSelect(row._id)}
                    />
                  </TableCol>,
                  <TableCol key="catalogNo" span={2}>
                    {row.catalogNumber}
                  </TableCol>,
                  <TableCol key="name" span={3}>
                    {row.name}
                  </TableCol>,
                  <TableCol key="quantity" span={2}>
                    <TextDiv align="center">{row.cartQuantity}</TextDiv>
                  </TableCol>,
                  <TableCol key="price" span={2}>
                    <TextDiv align="center">
                      {formatCurrency(row.grossPrice * row.cartQuantity)}
                    </TextDiv>
                  </TableCol>,
                  <TableCol key="confirmedQuantity" span={2}>
                    <TextDiv
                      color="black"
                      weight="bold"
                      bgColor={row.confirmedDeliveryDate ? 'lightGreen' : 'red'}
                      align="center"
                    >
                      {row.cartQuantity}
                    </TextDiv>
                  </TableCol>,
                  <TableCol key="deliveryDate" span={2}>
                    {row.isNotInStock ? (
                      <TextDiv align="center" color="red">
                        {t('notInStock')}
                      </TextDiv>
                    ) : (
                      (() => {
                        // If user selected a specific delivery date, show that
                        if (row.selectedDeliveryDate) {
                          return (
                            <TextDiv align="center">
                              {row.selectedDeliveryDate}
                            </TextDiv>
                          );
                        }

                        // Check if scheduleDetails match the cart quantity (including not in stock)
                        const scheduleTotal =
                          row.scheduleDetails?.reduce(
                            (sum, s) => sum + s.quantity,
                            0
                          ) || 0;
                        const totalWithNotInStock =
                          scheduleTotal + (row.notInStockQuantity || 0);
                        const hasValidSchedule =
                          row.scheduleDetails?.length > 0 &&
                          totalWithNotInStock === row.cartQuantity;
                        const hasPartialAvailability =
                          row.notInStockQuantity > 0;

                        if (
                          hasValidSchedule &&
                          (row.scheduleDetails.length > 1 ||
                            hasPartialAvailability)
                        ) {
                          // Multiple dates or partial availability - show quantity breakdown
                          return (
                            <div
                              style={{textAlign: 'center', fontSize: '12px'}}
                            >
                              {row.scheduleDetails.map((schedule, idx) => (
                                <div key={idx} style={{marginBottom: '4px'}}>
                                  <span style={{fontWeight: 500}}>
                                    {schedule.quantity} {t('pcs')}
                                  </span>
                                  <span style={{margin: '0 4px'}}>→</span>
                                  <span>{schedule.date}</span>
                                </div>
                              ))}
                              {hasPartialAvailability && (
                                <div style={{color: '#dc3545'}}>
                                  <span style={{fontWeight: 500}}>
                                    {row.notInStockQuantity} {t('pcs')}
                                  </span>
                                  <span style={{margin: '0 4px'}}>→</span>
                                  <span>{t('notInStock')}</span>
                                </div>
                              )}
                            </div>
                          );
                        } else if (
                          hasValidSchedule &&
                          row.scheduleDetails.length === 1
                        ) {
                          // Single date - just show the date without quantity
                          return (
                            <TextDiv align="center">
                              {row.scheduleDetails[0].date}
                            </TextDiv>
                          );
                        } else if (row.confirmedDeliveryDate) {
                          // Fallback to single confirmed date
                          return (
                            <TextDiv align="center">
                              {row.confirmedDeliveryDate}
                            </TextDiv>
                          );
                        } else {
                          return (
                            <TextDiv align="center" color="red">
                              {t('notInStock')}
                            </TextDiv>
                          );
                        }
                      })()
                    )}
                  </TableCol>,
                  <TableCol span={1} key="cart">
                    <RemoveFromCart id={row._id} onClick={handleRemove} />
                  </TableCol>,
                ]}
              </TableRow>
            ))
          }
        </Table>
      </CartContentTable>
      {items.length > 0 && (
        <Checkout
          allSelected={isAllSelected}
          comment={comment}
          orderNo={orderNo}
          disabled={disabled || isSubmitting}
          onSelectAll={handleSelectAll}
          onRemoveSelected={handleRemoveSelected}
          onCommentChange={handleCommentChange}
          onOrderNumberChange={handleOrderNumberChange}
          onSubmit={handleSubmit}
        />
      )}
    </Content>
  );
};

export default CartContainer;
